/**
 * Excel del reporte de morosidad con el formato que usan los negocios
 * (modelo "REPORTE PAGOS MENSUALES"): título combinado arriba, una fila por
 * préstamo con sus datos de cobro y bordes finos.
 *
 * Se usa xlsx-js-style (misma API que xlsx) porque la edición comunitaria de
 * xlsx no escribe estilos.
 */
import XLSX from "xlsx-js-style";

const BORDE = {
  top: { style: "thin", color: { rgb: "000000" } },
  bottom: { style: "thin", color: { rgb: "000000" } },
  left: { style: "thin", color: { rgb: "000000" } },
  right: { style: "thin", color: { rgb: "000000" } },
};
const MONTO = "#,##0.00";

const ESTILO_TITULO = {
  font: { bold: true, sz: 16 },
  alignment: { horizontal: "center", vertical: "center" },
};
const ESTILO_CABECERA = {
  font: { bold: true, sz: 13 },
  alignment: { horizontal: "center", vertical: "center" },
  border: BORDE,
};
const ESTILO_CELDA = { font: { sz: 11 }, alignment: { horizontal: "left" }, border: BORDE };
const ESTILO_NUM = { font: { sz: 11 }, alignment: { horizontal: "right" }, border: BORDE };
const ESTILO_TOTAL = { font: { bold: true, sz: 11 }, alignment: { horizontal: "right" }, border: BORDE };

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const txt = (v) => String(v ?? "").trim();

// Columnas en el mismo orden del modelo. `monto` = número con miles;
// `entero` = número sin decimales.
const COLUMNAS = [
  { titulo: "Cliente", ancho: 40, get: (r) => txt(r.cliente) },
  { titulo: "Cod", ancho: 9, get: (r) => txt(r.id) },
  // Comentario de cobro: la última nota de gestión del préstamo.
  { titulo: "ESTATUS", ancho: 30, get: (r) => txt(r.ultima_nota) },
  { titulo: "Estatus", ancho: 11, get: (r) => (r.estado_producto === "I" ? "Incautado" : "Normal") },
  { titulo: "Cartera", ancho: 18, get: (r) => txt(r.gestor) },
  { titulo: "Telefono", ancho: 13, get: (r) => txt(r.telefono) },
  { titulo: "Celular", ancho: 13, get: (r) => txt(r.celular) },
  { titulo: "Frecuencia", ancho: 12, get: (r) => txt(r.tipo) },
  { titulo: "Capital", ancho: 13, monto: true, get: (r) => num(r.capital_total) },
  { titulo: "Pago por cuota", ancho: 17, monto: true, get: (r) => num(r.cuota) },
  {
    titulo: "Cuotas",
    ancho: 9,
    get: (r) => `${num(r.cuotas_atrasadas)}/${num(r.cuotas_totales)}`,
  },
  { titulo: "Atrasadas", ancho: 12, entero: true, get: (r) => num(r.cuotas_atrasadas) },
  { titulo: "Dias", ancho: 8, entero: true, get: (r) => num(r.dias_atraso) },
  { titulo: "Sucursal", ancho: 30, get: (r) => txt(r.sucursal) },
  { titulo: "Vencido", ancho: 13, monto: true, get: (r) => num(r.atraso_total) },
];

const sello = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

/**
 * Arma el libro. Separado de la descarga para poder probarlo.
 * filas: filas normalizadas de la pantalla (ya filtradas por tipo).
 */
export function libroMorosidad(filas, { titulo = "REPORTE DE MOROSIDAD" } = {}) {
  // Agrupado como lo trabaja el cobrador: sucursal, cartera y de menos a
  // más atrasado.
  const orden = [...filas].sort(
    (a, b) =>
      txt(a.sucursal).localeCompare(txt(b.sucursal)) ||
      txt(a.gestor).localeCompare(txt(b.gestor)) ||
      num(a.cuotas_atrasadas) - num(b.cuotas_atrasadas) ||
      num(a.dias_atraso) - num(b.dias_atraso)
  );

  const totalCapital = orden.reduce((s, r) => s + num(r.capital_total), 0);
  const totalVencido = orden.reduce((s, r) => s + num(r.atraso_total), 0);

  const aoa = [
    [titulo],
    COLUMNAS.map((c) => c.titulo),
    ...orden.map((r) => COLUMNAS.map((c) => c.get(r))),
    COLUMNAS.map((c) => {
      if (c.titulo === "Cliente") return `TOTAL (${orden.length} clientes)`;
      if (c.titulo === "Capital") return totalCapital;
      if (c.titulo === "Vencido") return totalVencido;
      return "";
    }),
  ];
  const hoja = XLSX.utils.aoa_to_sheet(aoa);
  const ultima = aoa.length - 1;

  hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNAS.length - 1 } }];
  hoja["!cols"] = COLUMNAS.map((c) => ({ wch: c.ancho }));
  hoja["!rows"] = [{ hpt: 24 }, { hpt: 20 }];

  hoja.A1.s = ESTILO_TITULO;
  COLUMNAS.forEach((c, ci) => {
    const cab = XLSX.utils.encode_cell({ r: 1, c: ci });
    hoja[cab].s = ESTILO_CABECERA;
    for (let ri = 2; ri <= ultima; ri++) {
      const ref = XLSX.utils.encode_cell({ r: ri, c: ci });
      if (!hoja[ref]) hoja[ref] = { t: "s", v: "" };
      const esTotal = ri === ultima;
      if (c.monto) {
        hoja[ref].s = esTotal ? ESTILO_TOTAL : ESTILO_NUM;
        if (hoja[ref].t === "n") hoja[ref].z = MONTO;
      } else if (c.entero) {
        hoja[ref].s = { ...ESTILO_NUM, alignment: { horizontal: "center" } };
      } else {
        hoja[ref].s = esTotal ? { ...ESTILO_CELDA, font: { bold: true, sz: 11 } } : ESTILO_CELDA;
      }
    }
  });

  hoja["!autofilter"] = {
    ref: XLSX.utils.encode_range({ s: { r: 1, c: 0 }, e: { r: ultima - 1, c: COLUMNAS.length - 1 } }),
  };

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, "Morosidad");
  return libro;
}

export function descargarExcelMorosidad(filas, opciones = {}) {
  const libro = libroMorosidad(filas, opciones);
  const sufijo = opciones.archivo ? `_${opciones.archivo}` : "";
  XLSX.writeFile(libro, `Morosidad${sufijo}_${sello()}.xlsx`);
}
